import { API_BASE } from './api-base';
import { deduplicateRequest } from './request-deduplicator';

const getHeaders = (token?: string, clientId?: string) => {
  if (!token || !token.trim()) {
    throw new Error('Session expired. Please log in again.');
  }

  const headers: any = {
    'Content-Type': 'application/json',
  };
  headers['Authorization'] = `Bearer ${token}`;
  if (clientId) {
    headers['X-Client-ID'] = clientId;
  }
  return headers;
};

const parseJsonResponse = async (response: Response, endpoint: string) => {
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  const rawText = await response.text();

  if (!rawText) {
    return {} as any;
  }

  if (!contentType.includes('application/json')) {
    const preview = rawText.replace(/\s+/g, ' ').trim().slice(0, 120);
    throw new Error(
      `Unexpected non-JSON response from ${endpoint} (HTTP ${response.status}). ` +
      `Check API proxy/rewrite. Preview: ${preview}`
    );
  }

  try {
    return JSON.parse(rawText);
  } catch {
    const preview = rawText.replace(/\s+/g, ' ').trim().slice(0, 120);
    throw new Error(
      `Invalid JSON response from ${endpoint} (HTTP ${response.status}). Preview: ${preview}`
    );
  }
};

export const adminApi = {
  // ========== CLIENTS ==========
  
  createClient: async (token: string, clientData: any) => {
    try {
      console.log('🏢 Creating client...', { clientData });
      
      const response = await fetch(`${API_BASE}/api/clients`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(clientData),
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create client');
      
      console.log('✅ Client created successfully!', data.client);
      return data.client;
    } catch (error: any) {
      console.error('❌ Create client error:', error);
      throw error;
    }
  },

  getClients: async (token: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients`, {
        method: 'GET',
        headers: getHeaders(token),
      });
      const data = await parseJsonResponse(response, '/api/clients');
      if (!response.ok) throw new Error(data.error || 'Failed to fetch clients');
      return data.clients;
    } catch (error: any) {
      console.error('❌ Get clients error:', error);
      throw error;
    }
  },

  getClient: async (token: string, clientId: string) => {
    try {
      const cacheKey = `client:${clientId}`;
      return await deduplicateRequest(cacheKey, async () => {
        const response = await fetch(`${API_BASE}/api/clients/${clientId}`, {
          method: 'GET',
          headers: getHeaders(token),
        });
        const data = await parseJsonResponse(response, `/api/clients/${clientId}`);
        if (!response.ok) throw new Error(data.error || 'Failed to fetch client');
        return data.client;
      });
    } catch (error: any) {
      console.error('❌ Get client error:', error);
      throw error;
    }
  },

  updateClient: async (token: string, clientId: string, updates: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update client');
      return data.client;
    } catch (error: any) {
      console.error('❌ Update client error:', error);
      throw error;
    }
  },

  deleteClient: async (token: string, clientId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}`, {
        method: 'DELETE',
        headers: getHeaders(token),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete client');
      return data;
    } catch (error: any) {
      console.error('❌ Delete client error:', error);
      throw error;
    }
  },

  // ========== PLCs ==========

  createPLC: async (token: string, clientId: string, plcData: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs`, {
        method: 'POST',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(plcData),
      });
      const data = await parseJsonResponse(response, `/api/clients/${clientId}/plcs`);
      if (!response.ok) throw new Error(data.error || 'Failed to create PLC');
      return data.plc;
    } catch (error: any) {
      console.error('❌ Create PLC error:', error);
      throw error;
    }
  },

  getPLCs: async (token: string, clientId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await parseJsonResponse(response, `/api/clients/${clientId}/plcs`);
      if (!response.ok) throw new Error(data.error || 'Failed to fetch PLCs');
      return data.plcs || [];
    } catch (error: any) {
      console.error('❌ Get PLCs error:', error);
      throw error;
    }
  },

  updatePLC: async (token: string, clientId: string, plcId: string, updates: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/${plcId}`, {
        method: 'PUT',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(updates),
      });
      const data = await parseJsonResponse(response, `/api/clients/${clientId}/plcs/${plcId}`);
      if (!response.ok) throw new Error(data.error || 'Failed to update PLC');
      return data.plc;
    } catch (error: any) {
      console.error('❌ Update PLC error:', error);
      throw error;
    }
  },

  deletePLC: async (token: string, clientId: string, plcId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/${plcId}`, {
        method: 'DELETE',
        headers: getHeaders(token, clientId),
      });
      const data = await parseJsonResponse(response, `/api/clients/${clientId}/plcs/${plcId}`);
      if (!response.ok) throw new Error(data.error || 'Failed to delete PLC');
      return data;
    } catch (error: any) {
      console.error('❌ Delete PLC error:', error);
      throw error;
    }
  },

  // ========== PLC SECTIONS ==========

  getPLCSections: async (token: string, clientId: string, plcId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/${plcId}/sections`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch sections');
      return data.sections || [];
    } catch (error: any) {
      console.error('❌ Get sections error:', error);
      throw error;
    }
  },

  createPLCSection: async (token: string, clientId: string, plcId: string, sectionData: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/${plcId}/sections`, {
        method: 'POST',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(sectionData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create section');
      return data.section;
    } catch (error: any) {
      console.error('❌ Create section error:', error);
      throw error;
    }
  },

  updatePLCSection: async (token: string, clientId: string, plcId: string, sectionId: string, updates: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/${plcId}/sections/${sectionId}`, {
        method: 'PUT',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update section');
      return data.section;
    } catch (error: any) {
      console.error('❌ Update section error:', error);
      throw error;
    }
  },

  deletePLCSection: async (token: string, clientId: string, plcId: string, sectionId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/${plcId}/sections/${sectionId}`, {
        method: 'DELETE',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete section');
      return data;
    } catch (error: any) {
      console.error('❌ Delete section error:', error);
      throw error;
    }
  },

  // ========== TAGS ==========

  addTag: async (token: string, clientId: string, plcId: string, tag: any) => {
    const tags = await adminApi.addTags(token, clientId, plcId, [tag]);
    return tags[0] || null;
  },

  addTags: async (token: string, clientId: string, plcId: string, tags: any[]) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/${plcId}/tags`, {
        method: 'POST',
        headers: getHeaders(token, clientId),
        body: JSON.stringify({ tags }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to add tags');
      return data.tags || [];
    } catch (error: any) {
      console.error('❌ Add tags error:', error);
      throw error;
    }
  },

  getTags: async (token: string, clientId: string, plcId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/${plcId}/tags`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch tags');
      return data.tags || [];
    } catch (error: any) {
      console.error('❌ Get tags error:', error);
      throw error;
    }
  },

  updateTag: async (token: string, clientId: string, plcId: string, tagId: string, updates: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/${plcId}/tags/${tagId}`, {
        method: 'PUT',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update tag');
      return data.tag;
    } catch (error: any) {
      console.error('❌ Update tag error:', error);
      throw error;
    }
  },

  deleteTag: async (token: string, clientId: string, plcId: string, tagId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/${plcId}/tags/${tagId}`, {
        method: 'DELETE',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete tag');
      return data;
    } catch (error: any) {
      console.error('❌ Delete tag error:', error);
      throw error;
    }
  },

  
  // ========== USERS ==========

  getUsers: async (token: string, clientId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/users`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch users');
      return data.users || [];
    } catch (error: any) {
      console.error('❌ Get users error:', error);
      throw error;
    }
  },

  createUser: async (token: string, clientId: string, userData: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/users`, {
        method: 'POST',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(userData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create user');
      return data.user;
    } catch (error: any) {
      console.error('❌ Create user error:', error);
      throw error;
    }
  },

  updateUser: async (token: string, clientId: string, userId: string, updates: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/users/${userId}`, {
        method: 'PUT',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update user');
      return data.user;
    } catch (error: any) {
      console.error('❌ Update user error:', error);
      throw error;
    }
  },

  deleteUser: async (token: string, clientId: string, userId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/users/${userId}`, {
        method: 'DELETE',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete user');
      return data;
    } catch (error: any) {
      console.error('❌ Delete user error:', error);
      throw error;
    }
  },

  // ========== PDF TEMPLATES ==========

  getPDFTemplate: async (token: string, clientId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/pdf-template`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch PDF template');
      return data.template || data;
    } catch (error: any) {
      console.error('❌ Get PDF template error:', error);
      throw error;
    }
  },

  updatePDFTemplate: async (token: string, clientId: string, templateData: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/pdf-template`, {
        method: 'PUT',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(templateData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update PDF template');
      return data.template || data;
    } catch (error: any) {
      console.error('❌ Update PDF template error:', error);
      throw error;
    }
  },

  
  // ========== LIVE DATA ==========

  getLiveData: async (token: string, clientId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/live-data`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch live data');
      return data.liveData || [];
    } catch (error: any) {
      console.error('❌ Get live data error:', error);
      throw error;
    }
  },

  // ========== HISTORICAL DATA ==========

  getHistoricalData: async (token: string, clientId: string, options?: any) => {
    try {
      const params = new URLSearchParams();
      if (options?.startDate) params.append('startDate', options.startDate);
      if (options?.endDate) params.append('endDate', options.endDate);
      if (options?.plcId) params.append('plcId', options.plcId);
      if (options?.tagId) params.append('tagId', options.tagId);
      if (options?.tagIds) params.append('tagIds', options.tagIds);
      if (options?.sectionId) params.append('sectionId', options.sectionId);
      if (options?.interval) params.append('interval', options.interval);
      if (options?.limit) params.append('limit', options.limit);
      if (options?.offset) params.append('offset', options.offset);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/historical-data${queryString}`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await parseJsonResponse(response, `/api/clients/${clientId}/historical-data`);
      if (!response.ok) throw new Error(data.error || 'Failed to fetch historical data');
      return data;
    } catch (error: any) {
      console.error('❌ Get historical data error:', error);
      throw error;
    }
  },

  // Lightweight "latest reading per tag" for polling loops — avoids re-scanning/sorting
  // the full historical_data table just to get the current value of each tag.
  getPlcLatestValues: async (token: string, clientId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/latest-values`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await parseJsonResponse(response, `/api/clients/${clientId}/plcs/latest-values`);
      if (!response.ok) throw new Error(data.error || 'Failed to fetch latest PLC values');
      return data;
    } catch (error: any) {
      console.error('❌ Get PLC latest values error:', error);
      throw error;
    }
  },

  downloadHistoricalData: async (token: string, clientId: string, format: 'csv' | 'json' | 'pdf' | 'excel', options?: any) => {
    try {
      const params = new URLSearchParams();
      if (options?.startDate) params.append('startDate', options.startDate);
      if (options?.endDate) params.append('endDate', options.endDate);
      if (options?.plcId) params.append('plcId', options.plcId);
      if (options?.tagId) params.append('tagId', options.tagId);
      if (options?.tagIds) params.append('tagIds', options.tagIds);
      if (options?.sectionId) params.append('sectionId', options.sectionId);
      if (options?.interval) params.append('interval', options.interval);
      if (options?.limit) params.append('limit', String(options.limit));
      if (options?.offset) params.append('offset', String(options.offset));
      if (options?.tzOffset !== undefined) params.append('tzOffset', String(options.tzOffset));

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/download/historical-data/${format}${queryString}`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      
      if (!response.ok) {
        const data = await parseJsonResponse(response, `/api/clients/${clientId}/download/historical-data/${format}`);
        throw new Error(data.error || 'Failed to download data');
      }
      return await response.blob();
    } catch (error: any) {
      console.error('❌ Download historical data error:', error);
      throw error;
    }
  },

  // ========== PLANT KPI DASHBOARD ==========

  getPlantKpiConfig: async (token: string, clientId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plant-kpi-config`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await parseJsonResponse(response, `/api/clients/${clientId}/plant-kpi-config`);
      if (!response.ok) throw new Error(data.error || 'Failed to fetch plant KPI config');
      return data;
    } catch (error: any) {
      console.error('❌ Get plant KPI config error:', error);
      throw error;
    }
  },

  savePlantKpiConfig: async (token: string, clientId: string, config: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/plant-kpi-config`, {
        method: 'PUT',
        headers: { ...getHeaders(token, clientId), 'Content-Type': 'application/json' },
        body: JSON.stringify({ config }),
      });
      const data = await parseJsonResponse(response, `/api/clients/${clientId}/plant-kpi-config`);
      if (!response.ok) throw new Error(data.error || 'Failed to save plant KPI config');
      return data;
    } catch (error: any) {
      console.error('❌ Save plant KPI config error:', error);
      throw error;
    }
  },

  // ========== DASHBOARD WIDGETS ==========

  getDashboardWidgets: async (token: string, clientId: string, includeHidden = false) => {
    try {
      const params = includeHidden ? '?includeHidden=true' : '';
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/dashboard-widgets${params}`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await parseJsonResponse(response, `/api/clients/${clientId}/dashboard-widgets`);
      if (!response.ok) throw new Error(data.error || 'Failed to fetch dashboard widgets');
      return data.widgets || [];
    } catch (error: any) {
      console.error('❌ Get dashboard widgets error:', error);
      throw error;
    }
  },

  getDashboardWidgetData: async (token: string, clientId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/dashboard-widgets/data`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await parseJsonResponse(response, `/api/clients/${clientId}/dashboard-widgets/data`);
      if (!response.ok) throw new Error(data.error || 'Failed to fetch dashboard widget data');
      return data.widgets || [];
    } catch (error: any) {
      console.error('❌ Get dashboard widget data error:', error);
      throw error;
    }
  },

  getDeviceRuntimeStatus: async (token: string, clientId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/device-runtime-status`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await parseJsonResponse(response, `/api/clients/${clientId}/device-runtime-status`);
      if (!response.ok) throw new Error(data.error || 'Failed to fetch device runtime status');
      return data.statuses || [];
    } catch (error: any) {
      console.error('❌ Get device runtime status error:', error);
      throw error;
    }
  },

  createDashboardWidget: async (token: string, clientId: string, widget: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/dashboard-widgets`, {
        method: 'POST',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(widget),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create dashboard widget');
      return data.widget;
    } catch (error: any) {
      console.error('❌ Create dashboard widget error:', error);
      throw error;
    }
  },

  updateDashboardWidget: async (token: string, clientId: string, widgetId: string, widget: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/dashboard-widgets/${widgetId}`, {
        method: 'PUT',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(widget),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update dashboard widget');
      return data.widget;
    } catch (error: any) {
      console.error('❌ Update dashboard widget error:', error);
      throw error;
    }
  },

  deleteDashboardWidget: async (token: string, clientId: string, widgetId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/dashboard-widgets/${widgetId}`, {
        method: 'DELETE',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete dashboard widget');
      return data;
    } catch (error: any) {
      console.error('❌ Delete dashboard widget error:', error);
      throw error;
    }
  },

  // ========== ENERGY DATA ==========

  getEnergyData: async (token: string, clientId: string, options?: any) => {
    try {
      const params = new URLSearchParams();
      if (options?.startDate) params.append('startDate', options.startDate);
      if (options?.endDate) params.append('endDate', options.endDate);
      if (options?.limit) params.append('limit', options.limit);
      if (options?.offset) params.append('offset', options.offset);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/energy-data${queryString}`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch energy data');
      return data;
    } catch (error: any) {
      console.error('❌ Get energy data error:', error);
      throw error;
    }
  },

  getEnergyReadingsRealtime: async (token: string, clientId: string, minutes?: number) => {
    try {
      const cacheKey = `energy-readings-realtime:${clientId}:${minutes || '1440'}`;
      return await deduplicateRequest(cacheKey, async () => {
        const params = new URLSearchParams();
        if (minutes) params.append('minutes', String(minutes));
        
        const queryString = params.toString() ? `?${params.toString()}` : '';
        const response = await fetch(`${API_BASE}/api/clients/${clientId}/energy-readings-realtime${queryString}`, {
          method: 'GET',
          headers: getHeaders(token, clientId),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to fetch real-time energy readings');
        return data;
      });
    } catch (error: any) {
      console.error('❌ Get real-time energy readings error:', error);
      throw error;
    }
  },

  createEnergyData: async (token: string, clientId: string, payload: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/energy-data`, {
        method: 'POST',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create energy data');
      return data;
    } catch (error: any) {
      console.error('❌ Create energy data error:', error);
      throw error;
    }
  },

  getEnergyMeters: async (token: string, clientId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/energy-meters`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch energy meters');
      return data.meters || [];
    } catch (error: any) {
      console.error('❌ Get energy meters error:', error);
      throw error;
    }
  },

  getEnergyMeterTags: async (token: string, clientId: string, meterId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/energy-meters/${meterId}/tags`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch energy meter tags');
      return data.tags || [];
    } catch (error: any) {
      console.error('❌ Get energy meter tags error:', error);
      throw error;
    }
  },

  downloadEnergyData: async (token: string, clientId: string, format: 'csv' | 'json', options?: any) => {
    try {
      const params = new URLSearchParams();
      if (options?.startDate) params.append('startDate', options.startDate);
      if (options?.endDate) params.append('endDate', options.endDate);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await fetch(`${API_BASE}/api/clients/${clientId}/download/energy-data/${format}${queryString}`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to download energy data');
      }

      return await response.blob();
    } catch (error: any) {
      console.error('❌ Download energy data error:', error);
      throw error;
    }
  },

  // ========== MANUAL LOGS ==========

  getManualLogs: async (token: string, clientId: string, options?: any) => {
    try {
      const params = new URLSearchParams();
      if (options?.startDate) params.append('startDate', options.startDate);
      if (options?.endDate) params.append('endDate', options.endDate);
      if (options?.limit) params.append('limit', options.limit);
      if (options?.offset) params.append('offset', options.offset);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/manual-logs${queryString}`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch manual logs');
      return data;
    } catch (error: any) {
      console.error('❌ Get manual logs error:', error);
      throw error;
    }
  },

  createManualLog: async (token: string, clientId: string, payload: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/manual-logs`, {
        method: 'POST',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create manual log');
      return data;
    } catch (error: any) {
      console.error('❌ Create manual log error:', error);
      throw error;
    }
  },

  deleteManualLog: async (token: string, clientId: string, logId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/manual-logs/${logId}`, {
        method: 'DELETE',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete manual log');
      return data;
    } catch (error: any) {
      console.error('❌ Delete manual log error:', error);
      throw error;
    }
  },

  downloadManualLogs: async (token: string, clientId: string, format: 'csv' | 'json', options?: any) => {
    try {
      const params = new URLSearchParams();
      if (options?.startDate) params.append('startDate', options.startDate);
      if (options?.endDate) params.append('endDate', options.endDate);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await fetch(`${API_BASE}/api/clients/${clientId}/download/manual-logs/${format}${queryString}`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to download manual logs');
      }

      return await response.blob();
    } catch (error: any) {
      console.error('❌ Download manual logs error:', error);
      throw error;
    }
  },

  // ========== SCRAP DATA ==========

  getScrapData: async (token: string, clientId: string, options?: any) => {
    try {
      const params = new URLSearchParams();
      if (options?.startDate) params.append('startDate', options.startDate);
      if (options?.endDate) params.append('endDate', options.endDate);
      if (options?.limit) params.append('limit', options.limit);
      if (options?.offset) params.append('offset', options.offset);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/scrap-data${queryString}`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch scrap data');
      return data;
    } catch (error: any) {
      console.error('❌ Get scrap data error:', error);
      throw error;
    }
  },

  createScrapData: async (token: string, clientId: string, payload: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/scrap-data`, {
        method: 'POST',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create scrap entry');
      return data;
    } catch (error: any) {
      console.error('❌ Create scrap data error:', error);
      throw error;
    }
  },

  deleteScrapData: async (token: string, clientId: string, entryId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/scrap-data/${entryId}`, {
        method: 'DELETE',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete scrap entry');
      return data;
    } catch (error: any) {
      console.error('❌ Delete scrap data error:', error);
      throw error;
    }
  },


  downloadScrapData: async (token: string, clientId: string, format: 'csv' | 'json', options?: any) => {
    try {
      const params = new URLSearchParams();
      if (options?.startDate) params.append('startDate', options.startDate);
      if (options?.endDate) params.append('endDate', options.endDate);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await fetch(`${API_BASE}/api/clients/${clientId}/download/scrap-data/${format}${queryString}`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to download scrap data');
      }

      return await response.blob();
    } catch (error: any) {
      console.error('Failed to download scrap data error:', error);
      throw error;
    }
  },

  logClientAuditEvent: async (token: string, clientId: string, payload: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/audit-events`, {
        method: 'POST',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to record audit event');
      return data;
    } catch (error: any) {
      console.error('❌ Client audit log error:', error);
      throw error;
    }
  },

  // ========== REPORT TEMPLATES ==========

  getReportTemplates: async (token: string, clientId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/report-templates`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch report templates');
      return data.templates || [];
    } catch (error: any) {
      console.error('Get report templates error:', error);
      throw error;
    }
  },

  createReportTemplate: async (token: string, clientId: string, templateData: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/report-templates`, {
        method: 'POST',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(templateData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create report template');
      return data;
    } catch (error: any) {
      console.error('Create report template error:', error);
      throw error;
    }
  },

  updateReportTemplate: async (token: string, clientId: string, templateId: string, updates: any) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/report-templates/${templateId}`, {
        method: 'PUT',
        headers: getHeaders(token, clientId),
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update report template');
      return data;
    } catch (error: any) {
      console.error('Update report template error:', error);
      throw error;
    }
  },

  deleteReportTemplate: async (token: string, clientId: string, templateId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/report-templates/${templateId}`, {
        method: 'DELETE',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete report template');
      return data;
    } catch (error: any) {
      console.error('Delete report template error:', error);
      throw error;
    }
  },

  getReportData: async (token: string, clientId: string, templateId: string, from: string, to: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/clients/${clientId}/report-templates/${templateId}/data?from=${from}&to=${to}`, {
        method: 'GET',
        headers: getHeaders(token, clientId),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch report data');
      return data;
    } catch (error: any) {
      console.error('Get report data error:', error);
      throw error;
    }
  },


};

export default adminApi;
